import { Injectable, inject } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs';

const CHUNK_LOAD_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Loading chunk .*failed|ChunkLoadError|error loading dynamically imported module/i;
const RELOAD_GUARD_KEY = 'pp_chunk_reload_ts';
const RELOAD_GUARD_WINDOW_MS = 10000;

/**
 * After a redeploy, a tab left open still references old chunk hashes that no
 * longer exist on the server. Any lazy route navigation then fails with
 * "Failed to fetch dynamically imported module". Detect that failure and
 * reload once to pick up the freshly built index.html / chunks.
 */
@Injectable({ providedIn: 'root' })
export class ChunkErrorHandlerService {
  private readonly router = inject(Router);

  init(): void {
    this.router.events
      .pipe(filter((event): event is NavigationError => event instanceof NavigationError))
      .subscribe((event) => this.handlePossibleChunkError(event.error));

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => this.handleRejection(event));
  }

  protected handleRejection(event: PromiseRejectionEvent): void {
    this.handlePossibleChunkError(event.reason);
  }

  private handlePossibleChunkError(error: unknown): void {
    const message = this.extractMessage(error);
    if (!message || !CHUNK_LOAD_ERROR_PATTERN.test(message)) return;
    this.reloadOnce();
  }

  private extractMessage(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;
    return '';
  }

  private reloadOnce(): void {
    const lastReload = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
    const now = Date.now();
    if (now - lastReload < RELOAD_GUARD_WINDOW_MS) return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
    this.triggerReload();
  }

  protected triggerReload(): void {
    window.location.reload();
  }
}
